import { produce } from "immer";
import cloneDeep from "lodash/cloneDeep";
import groupBy from "lodash/groupBy";
import orderBy from "lodash/orderBy";
import set from "lodash/set";
import { defineStore } from "pinia";

import {
  CommunicationType,
  type LogEndpointType,
} from "@/enums/communicationLogs";
import { getObjectKeys } from "@/helpers/common";
import { LOG_ENDPOINT_TYPE_TO_LOG_TYPE } from "@/helpers/constants/communicationLogs";
import { formatDateCustom } from "@/helpers/formatting";
import communicationService from "@/services/modules/communication";
import emailService from "@/services/modules/emails";

import type { IApplication } from "@/models/applications";
import type {
  CallLog,
  CallLogData,
  CallNotePayload,
  CallOutPayload,
  CommunicationFilters,
  CommunicationLogBase,
  CommunicationLogComment,
  CommunicationLogEventData,
  CommunicationLogFilterableData,
  CommunicationLogState,
  ContactUser,
  CreateTaskPayload,
  SmsLogData,
  Task,
  TaskLog,
  TaskLogData,
} from "@/models/communicationLogs";
import type { AxiosError } from "axios";

interface CommentPayload {
  logId: CommunicationLogBase["id"];
  type: LogEndpointType;
  comment: CommunicationLogComment["comment"];
}

const PER_PAGE_STEP = "15";
const THREADS_PER_PAGE_STEP = "30";

const getDefaultState = (): CommunicationLogState => ({
  logs: [],
  emailThreads: [],
  meta: null,
  hasNewLogs: false,
  canLoadMore: true,
  hasNoMoreData: false,
  activityHubPage: { active_tabs: [] },
  dealActivityHub: { active_tabs: [] },
  allActivityModalDealId: null,
  dealActivityActiveTypes: [CommunicationType.note],
});

export const useCommunicationStore = defineStore("communication", {
  state: getDefaultState,
  getters: {
    logsGroupedByDate({ logs }) {
      const pattern = "dd MMM yyyy";
      // We want both "today" and "logCreatedAt" to be in current user's time zone
      const today = formatDateCustom(new Date(), pattern, true);

      return groupBy(logs, (log) => {
        const logCreatedAt = formatDateCustom(
          log.data.created_at,
          pattern,
          true
        );
        return today === logCreatedAt ? "today" : logCreatedAt;
      });
    },
    canClientAccessActivityHub(store) {
      return Boolean(store.activityHubPage.active_tabs.length);
    },
    canClientAccessDealActivityHubTab(store) {
      return Boolean(store.dealActivityHub.active_tabs.length);
    },
  },
  actions: {
    async getCommunicationList(
      filters: Partial<CommunicationFilters> = {},
      isThreadsPage = false
    ) {
      this.canLoadMore = false;
      // preparing params with proper values
      const params: Partial<CommunicationFilters> = cloneDeep(filters);
      params.per_page = isThreadsPage ? THREADS_PER_PAGE_STEP : PER_PAGE_STEP;
      const arrayParams: Array<keyof CommunicationFilters> = [
        "application_ids",
        "assignee_ids",
        "call_session_caller_ids",
      ];
      arrayParams.forEach((param) => {
        const value = params[param];
        if (value) {
          const valueArray = Array.isArray(value) ? value : [value];
          Object.assign(params, { [param]: valueArray });
        }
      });

      if (isThreadsPage) {
        params.is_thread = "1";
      }
      const response = await communicationService.getCommunicationList(params);
      this.meta = response.meta;
      const { data, meta } = response;
      this.canLoadMore = !!data?.length && meta.current_page !== meta.last_page;
      this.hasNoMoreData =
        !data?.length || meta.current_page === meta.last_page;

      /*
       * hasNewLogs flag means we need to add new items to the list
       * and sort them by created_at
       */
      if (this.hasNewLogs) {
        const newItems = data.filter(
          (item) => !this.logs.some(({ id }) => id === item.id)
        );

        newItems.forEach((newItem) => {
          // sms conversations are being duplicated
          // due to each log having a new id despite being in the same conversation
          if ("sms_conversation_id" in newItem.data) {
            const indexOfOldItem = this.logs.findIndex(
              (log) =>
                (log.data as SmsLogData)?.sms_conversation_id ===
                (newItem.data as SmsLogData)?.sms_conversation_id
            );
            if (indexOfOldItem > -1) {
              // remove the old one
              this.logs.splice(indexOfOldItem, 1);
            }
          }
        });

        this.logs = orderBy(
          [...this.logs, ...newItems],
          (item) => new Date(item.data.created_at ?? ""),
          filters.sort_by === "created_at" ? "asc" : "desc"
        );

        return;
      }

      if (!params.page || params.page === "1") {
        this.logs = data;
        return;
      }
      // if page > 1, we append logs
      this.logs.push(...response.data);
    },

    async getEmailThreads(filters: Partial<CommunicationFilters> = {}) {
      this.canLoadMore = false;
      // preparing params with proper values
      const params: Partial<CommunicationFilters> = cloneDeep(filters);
      params.per_page = THREADS_PER_PAGE_STEP;
      const arrayParams: Array<keyof CommunicationFilters> = [
        "application_ids",
        "recipient_ids",
        "sender_ids",
        "assignee_ids",
      ];
      arrayParams.forEach((param) => {
        const value = params[param];
        const valueArray = Array.isArray(value) ? value : [value];

        if (value) {
          Object.assign(params, { [param]: valueArray });
        }
      });

      const response = await emailService.getEmailThreads(params);
      this.meta = response.meta;
      this.canLoadMore = !!response.data?.length;
      this.hasNoMoreData = !response.data?.length;

      if (this.hasNewLogs) {
        const newItems = response.data.filter(
          (item) => !this.emailThreads.some(({ id }) => id === item.id)
        );
        this.emailThreads.unshift(...newItems);
        return;
      }

      if (!params.page || params.page === "1") {
        this.emailThreads = response.data;
        return;
      }

      response.data.forEach((emailThread) => {
        const index = this.emailThreads.findIndex(
          (thread) => thread.id === emailThread.id
        );
        if (index === -1) {
          this.emailThreads.push(emailThread);
        } else {
          this.emailThreads[index] = emailThread;
        }
      });
    },

    async callOut(applicationId: IApplication["id"], payload: CallOutPayload) {
      try {
        return await communicationService.callOut(applicationId, payload);
      } catch (error) {
        if (payload.provider !== "ring_central") {
          return;
        }
        const ringCentralError = error as AxiosError<{ auth_link?: string }>;
        const authLink = ringCentralError?.response?.data?.auth_link;
        if (authLink) {
          window.open(authLink, "_blank");
        }
      }
    },

    async createTask(payload: CreateTaskPayload) {
      await communicationService.createTask(payload);
    },

    async updateTask(
      taskId: Task["id"],
      payload: Partial<TaskLogData & { description: string }>,
      user?: ContactUser
    ) {
      await communicationService.updateTask(taskId, payload);
      const currentTask = this.logs.find(
        (task) => task.data.id === taskId
      ) as TaskLog;
      if (!currentTask) {
        return;
      }

      currentTask.data = {
        ...currentTask.data,
        ...payload,
        body: payload.description ?? currentTask.data.body,
      };
      if (user) {
        currentTask.data.assignee = user;
      }
    },

    async deleteTask(taskId: Task["id"]) {
      await communicationService.deleteTask(taskId);
      this.logs = this.logs.filter((task) => task.data.id !== taskId);
    },

    async createCallNote(
      applicationId: IApplication["id"],
      payload: CallNotePayload
    ) {
      return await communicationService.createCallNote(applicationId, payload);
    },

    async updateCallNote(
      callSessionId: CallLogData["id"],
      payload: CallNotePayload
    ) {
      const updatedNote = await communicationService.updateCallNote(
        callSessionId,
        payload
      );

      this.logs = produce(this.logs, (draftLogs: CallLog[]) => {
        const log = draftLogs.find((log) => log.data.id == callSessionId);

        if (!log) {
          return;
        }

        log.data.body = updatedNote.body;
        log.data.call_disposition = updatedNote.call_disposition;
      });
    },

    async handleWebsocketLogUpdateEvent(payload: {
      id: CommunicationLogBase["id"];
      event: CommunicationLogEventData["event"];
      data: CommunicationLogFilterableData;
    }) {
      const logToUpdate = this.logs.find((log) => log.id === payload.id);

      if (!logToUpdate || payload.event !== "updated") {
        return;
      }

      const log = await communicationService.getCommunicationLog(payload.id);
      this.logs = produce(this.logs, (draft) => {
        const index = draft.findIndex((savedLog) => savedLog.id === log.id);
        draft[index] = log;
      });
    },

    getLogIndex(
      context: LogEndpointType,
      id: CommunicationLogBase["data"]["id"]
    ) {
      return this.logs.findIndex((log) => {
        const { data, type } = log;
        return (
          type === LOG_ENDPOINT_TYPE_TO_LOG_TYPE[context] && data.id === id
        );
      });
    },

    async createComment(payload: CommentPayload) {
      const newComment = await communicationService.createComment(
        payload.type,
        payload.logId,
        payload.comment
      );

      const index = this.getLogIndex(payload.type, payload.logId);
      if (index === -1 || !this.logs[index].data.comments) {
        return;
      }
      this.logs[index].data.comments.unshift(newComment);
    },

    async updateComment(
      payload: CommentPayload & {
        commentId: CommunicationLogComment["id"];
      }
    ) {
      const updatedComment = await communicationService.updateComment(
        payload.type,
        payload.logId,
        payload.commentId,
        payload.comment
      );
      const index = this.getLogIndex(payload.type, payload.logId);
      if (index === -1 || !this.logs[index].data.comments) {
        return;
      }
      const commentIndex = this.logs[index].data.comments.findIndex(
        (comment) => comment.id === payload.commentId
      );
      if (commentIndex === -1) {
        return;
      }
      this.logs[index].data.comments[commentIndex] = updatedComment;
    },

    async deleteComment(
      payload: Omit<CommentPayload, "comment"> & {
        commentId: CommunicationLogComment["id"];
      }
    ) {
      await communicationService.deleteComment(
        payload.type,
        payload.logId,
        payload.commentId
      );
      const index = this.getLogIndex(payload.type, payload.logId);
      if (index === -1) {
        return;
      }
      const logData = this.logs[index].data;
      logData.comments =
        logData.comments?.filter(
          (comment) => comment.id !== payload.commentId
        ) ?? [];
    },
    async getActivityHubInfo(applicationId?: string) {
      const activityHubToUpdate: keyof CommunicationLogState = applicationId
        ? "dealActivityHub"
        : "activityHubPage";
      this[activityHubToUpdate] =
        (await communicationService.getActivityHubInfo(applicationId))?.data ??
        getDefaultState()[activityHubToUpdate];
    },
    setAllActivityModalDealId(dealId: string | null) {
      this.allActivityModalDealId = dealId;
    },
    resetStore(exceptionKeys: Array<keyof CommunicationLogState> = []) {
      const defaultState = getDefaultState();
      const keys = getObjectKeys(defaultState);
      keys.forEach((key) => {
        if (!exceptionKeys.includes(key)) {
          set(this, key, defaultState[key]);
        }
      });
    },
    updateLog({
      id,
      key,
      value,
    }: {
      id: number;
      key: "has_summary" | "has_transcription";
      value: boolean;
    }) {
      this.logs = produce(this.logs, (draftLogs: CallLog[]) => {
        const log = draftLogs.find((log) => log.id == id);

        if (!log) {
          return;
        }

        log.data[key] = value;
      });
    },
  },
});
